import type { InferGetServerSidePropsType, NextPage } from 'next';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';
import type React from 'react';
import { request } from 'undici';

import { ErrorBoundary } from '#components/Error/ErrorBoundary';
import { Traffic } from '#components/Traffic/Traffic';
import { TrafficSection } from '#components/Traffic/TrafficSection';
import { TrafficSectionItem } from '#components/Traffic/TrafficSectionItem';
import { NAVIGATION_ITEM_TRAFFIC } from '#config/constants';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import type { CacheTag } from '#cutils/page-context/edge-cache-tags';
import { createDefaultGetServerSideProps } from '#cutils/page-context/server-side-props';
import { buildContentPageSeoConfig } from '#cutils/seo/buildContentPageSeoConfig';
import { envConfigServer } from '#sconfig/env.server';
import { ServiceUnavailableError } from '#sutils/service-unavailable-error';
import type { TrafficIcon } from '#types/enums';
import type { TrafficAPIResponse } from '#types/traffic';
import { logger } from '#utils/logging/logger';
import { formatTimestamp } from '#utils/time/formatTimestamp';
import { formatTimestampDateTime } from '#utils/time/formatTimestampDateTime';
import { useTracktor } from 'react-tracktor';

import styles from './verkehrsmeldungen.module.scss';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const errorFallback = (
	<Traffic>
		<h1 className={`${styles.title} ${styles.hasNoContent}`}>Es konnten keine Verkehrsnachrichten gefunden werden.</h1>
	</Traffic>
);

type TrafficData = {
	timestamp: number | null;
	sections: {
		id: string;
		title: string;
		items: {
			title: string;
			text: string;
			icon: TrafficIcon;
			timestamp: string;
			id: string;
		}[];
	}[];
};

const SEO_DESCRIPTION =
	'Alle Staus & Verkehrsmeldungen in Bayern. Aktuelle Verkehrsinfos auf einen Klick. So meiden Sie Staus & Verkehrsbehinderungen - im Berufsverkehr und in den Schulferien!';
const SEO_TITLE = 'Verkehrsmeldungen';

const TrafficPageContent: React.FC<Props> = ({ trafficData, formattedTimestamp, formattedTimestampDateTime }) => {
	const { constants } = useEnvironment();
	useTracktor({ pageViewData: { data: NAVIGATION_ITEM_TRAFFIC, event: { type: 'PageViewEvent' } } });

	const mappedSections = trafficData.map((section) => {
		const mappedSectionItems = section.items.map((item) => {
			return <TrafficSectionItem icon={item.icon} text={item.text} timestamp={item.timestamp} title={item.title} key={item.id} />;
		});

		return (
			<TrafficSection title={section.title} key={section.id}>
				{mappedSectionItems}
			</TrafficSection>
		);
	});

	const seoConfig = buildContentPageSeoConfig({
		description: SEO_DESCRIPTION,
		title: SEO_TITLE,
		url: `${constants.appUrl}/verkehrsmeldungen`,
	});

	return (
		<>
			<Head>{generateNextSeo(seoConfig)}</Head>

			<Traffic formattedTimestamp={formattedTimestamp} formattedTimestampDateTime={formattedTimestampDateTime}>
				{mappedSections}
			</Traffic>
		</>
	);
};

const Page: NextPage<Props> = (props) => (
	<ErrorBoundary fallback={errorFallback}>
		<TrafficPageContent {...props} />
	</ErrorBoundary>
);

async function getTrafficData(): Promise<TrafficData | { notFound: true }> {
	const response = await request(envConfigServer.ENDPOINT_TRAFFIC, { method: 'GET' });
	if (response.statusCode < 200 || response.statusCode >= 300) {
		const responseContent = await response.body.text();

		if (response.statusCode === 503) {
			throw new ServiceUnavailableError();
		} else {
			throw new Error(
				`Could not get traffic data from ${envConfigServer.ENDPOINT_TRAFFIC} status: ${response.statusCode}, ${responseContent}`
			);
		}
	} else {
		// check if the response is valid
		if (response.headers['content-type']?.includes('application/json')) {
			const data = await response.body.json();
			if (
				typeof data === 'object' &&
				data != null &&
				'bmtKategorien' in data &&
				data.bmtKategorien != null &&
				typeof data.bmtKategorien === 'object'
			) {
				return mapTrafficData(data as TrafficAPIResponse);
			} else {
				throw new Error('Traffic Response has not the expected shape');
			}
		} else {
			throw new Error(`Traffic API returned unknown content type: ${response.headers['content-type']}`);
		}
	}
}

function mapTrafficData(data: TrafficAPIResponse): TrafficData {
	return {
		timestamp: data?.exportTimestamp ? parseInt(data.exportTimestamp, 10) : null,
		sections: data.bmtKategorien.map((section) => {
			return {
				id: section.kategorieId,
				title: section.titel,
				items:
					section.meldungen?.map((message) => {
						return {
							id: message.metasMeldungsID,
							title: message.headline,
							text: message.absatz[0],
							icon: message.visualisierung,
							timestamp: message.meldungsZeit,
						};
					}) ?? [],
			};
		}),
	};
}

export const getServerSideProps = createDefaultGetServerSideProps(async () => {
	if (envConfigServer.IS_TRAFFIC_HIDDEN) {
		return {
			notFound: true,
		};
	}

	const navigationTitle = NAVIGATION_ITEM_TRAFFIC.pageTitle;

	try {
		const response = await getTrafficData();

		if ('notFound' in response) {
			return response;
		}

		const formattedTimestamp = response.timestamp ? formatTimestamp(response.timestamp, new Date(Date.now())) : null;
		const formattedTimestampDateTime = response.timestamp ? formatTimestampDateTime(response.timestamp) : null;

		return {
			props: {
				trafficData: response.sections,
				navigationTitle,
				formattedTimestamp,
				formattedTimestampDateTime,
				cacheTags: new Set<CacheTag>(),
			},
		};
	} catch (error: any) {
		logger.error(error, 'Error resolving verkehrsmeldungen page');
		throw error;
	}
});

export default Page;
